import { FunctionComponent, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import {
	StoryboardScene,
	WizardTimelineClip,
} from "@/pages/Private/redux/media/videoWizard.schema";
import { classNames } from "@/utils/classNames";

import { formatSeconds } from "../wizard.utils";

interface TimelineProps {
	clips: WizardTimelineClip[];
	scenesById: Map<number, { scene: StoryboardScene; number: number }>;
	selectedPosition: number;
	disabled?: boolean;
	onSelect: (position: number) => void;
	onMove: (from: number, to: number) => void;
}

/** Pixels per second of kept material, so a longer clip is visibly a longer clip. */
const SECOND_WIDTH = 26;
const MIN_CLIP_WIDTH = 92;

/**
 * The strip of clips, reorderable two ways.
 *
 * Drag-and-drop is the mockup's interaction and the one most authors will reach for, but
 * the ◀ ▶ buttons are not a fallback for it — they are the path that works with a keyboard,
 * on a touch screen and with a screen reader, and there is no sortable-list dependency in
 * this repo that would have given us any of that for free.
 *
 * The strip computes nothing about the cut: the widths and totals are read off the clips
 * the server sent, and every reorder goes back through the parent's single source of truth.
 */
export const Timeline: FunctionComponent<TimelineProps> = ({
	clips,
	scenesById,
	selectedPosition,
	disabled,
	onSelect,
	onMove,
}) => {
	const { t } = useTranslation();
	const ts = useCallback(
		(key: string, options?: Record<string, unknown>) =>
			t(`videoWizard.steps.cutExport.${key}`, options ?? {}),
		[t]
	);

	const [dragFrom, setDragFrom] = useState<number | null>(null);
	const [dragOver, setDragOver] = useState<number | null>(null);

	return (
		<div className="overflow-x-auto pb-1">
			<ul className="flex min-w-max items-stretch gap-1.5" role="list">
				{clips.map((clip, position) => {
					const entry = scenesById.get(clip.storyboardSceneId);

					if (!entry) {
						return null;
					}

					const { scene, number } = entry;
					const kept = Math.round((clip.outSeconds - clip.inSeconds) * 10) / 10;
					const isTrimmed = clip.inSeconds > 0 || clip.outSeconds < scene.durationSeconds;
					const isSelected = position === selectedPosition;
					const inPercent = (clip.inSeconds / scene.durationSeconds) * 100;
					const outPercent = (clip.outSeconds / scene.durationSeconds) * 100;

					return (
						<li
							key={clip.storyboardSceneId}
							className={classNames(
								"relative flex shrink-0 flex-col justify-between overflow-hidden rounded-card border-2 bg-surface p-2 text-left transition",
								isSelected ? "border-accent" : "border-border-soft",
								dragOver === position && dragFrom !== position && "border-accent-strong",
								dragFrom === position && "opacity-50"
							)}
							style={{
								width: Math.max(kept * SECOND_WIDTH, MIN_CLIP_WIDTH),
								height: 104,
							}}
							onDragEnd={() => {
								setDragFrom(null);
								setDragOver(null);
							}}
							onDragLeave={() => setDragOver((current) => (current === position ? null : current))}
							onDragOver={(event) => {
								event.preventDefault();
								setDragOver(position);
							}}
							onDrop={(event) => {
								event.preventDefault();
								setDragOver(null);

								// The transfer payload backs the state up: state set on a deferred
								// tick can lose a race with a very fast drop.
								const carried = Number(event.dataTransfer.getData("text/plain"));
								const from = dragFrom ?? (Number.isInteger(carried) ? carried : null);

								if (from !== null && from !== position) {
									onMove(from, position);
								}

								setDragFrom(null);
							}}
						>
							{/* The clip's own frame, dimmed where the cut throws material away. */}
							{!!scene.image?.assetUrl && (
								<img
									alt=""
									className="absolute inset-0 h-full w-full object-cover opacity-40"
									src={scene.image.assetUrl}
								/>
							)}
							{clip.inSeconds > 0 && (
								<span
									className="absolute inset-y-0 left-0 bg-ink/45 bg-[repeating-linear-gradient(45deg,transparent,transparent_4px,rgba(255,255,255,.18)_4px,rgba(255,255,255,.18)_8px)]"
									style={{ width: `${inPercent}%` }}
								/>
							)}
							{clip.outSeconds < scene.durationSeconds && (
								<span
									className="absolute inset-y-0 right-0 bg-ink/45 bg-[repeating-linear-gradient(45deg,transparent,transparent_4px,rgba(255,255,255,.18)_4px,rgba(255,255,255,.18)_8px)]"
									style={{ width: `${100 - outPercent}%` }}
								/>
							)}

							<button
								aria-pressed={isSelected}
								className="relative z-10 flex-1 text-left"
								draggable={!disabled}
								title={ts("clipHint")}
								type="button"
								onClick={() => onSelect(position)}
								onDragStart={(event) => {
									event.dataTransfer.effectAllowed = "move";
									// Firefox refuses to start a drag without payload on the transfer.
									event.dataTransfer.setData("text/plain", String(position));
									// Deferred: a synchronous re-render inside dragstart mutates the
									// dragged element (the opacity class), and Chrome cancels the
									// drag on the spot — the bug that made this strip undraggable.
									window.setTimeout(() => setDragFrom(position), 0);
								}}
							>
								<span className="block text-xs font-bold text-ink">
									{ts("clipLabel", { number })}
								</span>
								<span className="mt-0.5 block text-xs font-semibold text-subtle">
									{isTrimmed
										? ts("clipTrimmed", {
												kept: formatSeconds(kept),
												total: formatSeconds(scene.durationSeconds),
											})
										: formatSeconds(kept)}
								</span>
							</button>

							{/* Not a fallback for the drag — the accessible way to do the same thing. */}
							<div className="relative z-10 flex gap-1">
								<button
									aria-label={ts("moveLeft", { number })}
									className="rounded bg-white/90 px-1.5 py-0.5 text-xs font-bold text-ink disabled:opacity-40"
									disabled={disabled || position === 0}
									type="button"
									onClick={() => onMove(position, position - 1)}
								>
									◀
								</button>
								<button
									aria-label={ts("moveRight", { number })}
									className="rounded bg-white/90 px-1.5 py-0.5 text-xs font-bold text-ink disabled:opacity-40"
									disabled={disabled || position === clips.length - 1}
									type="button"
									onClick={() => onMove(position, position + 1)}
								>
									▶
								</button>
							</div>
						</li>
					);
				})}
			</ul>
		</div>
	);
};
